import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { fromPromise } from 'xstate';

import { useNoticeBannersStackContext } from '@sg/garnish';
import { type Order } from '@sg/graphql-schema';

import { useGroupOrderingContent } from '@order/features-group-ordering';
import {
  type ConvertToGroupOrderErrorParams,
  type ConvertToGroupOrderParams,
  type ConvertToGroupOrderResponse,
  type OrderingMachineAction,
} from '@order/features-ordering';

import { useGroupOrderTelemetry } from '../../../../hooks/useGroupOrderTelemetry';
import { useBagConvertToGroupOrderMutation } from '../../GraphQL/BagGroupOrder.generated';
import { prepareCart } from '../../utils';

// ─────────────────────────────────────────────────────────────────────────────

export const useConvertToGroupOrder = () => {
  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { mappedContent: groupOrderingContent } = useGroupOrderingContent();
  const [, executeConvertToGroupOrder] = useBagConvertToGroupOrderMutation();
  const { trackCreateSuccess, trackCreateFailure } = useGroupOrderTelemetry();

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const convertToGroupOrder = useMemo(
    () =>
      fromPromise<ConvertToGroupOrderResponse, ConvertToGroupOrderParams>(async ({ input }) => {
        const { hostName } = input;

        const response = await executeConvertToGroupOrder({
          input: { multipartyOrderDetails: { hostName } },
        });

        const result = response.data?.convertToMultipartyOrder;

        if (result?.__typename === 'MultipartyOrderNotEnabled') {
          throw new Error(MULTIPARTY_ORDER_NOT_ENABLED_ERROR);
        }

        const order =
          result?.__typename === 'ConvertToMultipartyOrderSuccess' ? result.order : undefined;

        if (response.error || !order) throw new Error('Unable to create group order');

        const cart = prepareCart(order as Partial<Order>);

        if (!cart.groupOrderJoinCode) throw new Error('Unable to create group order');

        trackCreateSuccess();

        return { cart };
      }),
    [executeConvertToGroupOrder, trackCreateSuccess],
  );

  const handleConvertToGroupOrderError = useCallback<
    OrderingMachineAction<ConvertToGroupOrderErrorParams>
  >(
    (_args, params) => {
      trackCreateFailure();

      const isGroupOrderingNotEnabled = params.errorMessage === MULTIPARTY_ORDER_NOT_ENABLED_ERROR;

      const errorMessage = isGroupOrderingNotEnabled
        ? groupOrderingContent.groupOrderingNotEnabledErrorMessage
        : formatMessage(messages.convertGroupOrderError);

      addNoticeBanner(
        {
          id: GROUP_ORDER_CONVERT_ERROR_BANNER_ID,
          text: errorMessage,
          palette: 'caution',
          autoHideTimeout: 1800,
        },
        true,
      );
    },
    [
      addNoticeBanner,
      formatMessage,
      groupOrderingContent.groupOrderingNotEnabledErrorMessage,
      trackCreateFailure,
    ],
  );

  return {
    convertToGroupOrder,
    handleConvertToGroupOrderError,
  };
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  convertGroupOrderError: {
    defaultMessage: 'Unable to create group order',
    description: 'Bag | Convert to group order | Error message',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const GROUP_ORDER_CONVERT_ERROR_BANNER_ID = 'group-order-convert-error-banner';

const MULTIPARTY_ORDER_NOT_ENABLED_ERROR = 'multiparty-order-not-enabled';
